import { useDroppable } from "@dnd-kit/core";
import {
  Box,
  BoxProps,
  darken,
  lighten,
  Typography,
  useTheme,
} from "@mui/material";
import { Spacer, Toolbar, useDialog } from "common";
import Dialog from "components/DraggableDialog/DraggableDialog";
import EventGroupForm from "features/event-groups/EventGroupForm";
import {
  EditMode,
  useEventEditorContext,
} from "features/events/Editor/EventEditorContext";
import { EventItem } from "features/events/eventApi";
import moment from "moment";
import { useMemo } from "react";
import { useEventCalculator } from "../../useEventCalculator";
import { BoatGroup } from "../cells/useStartBoatCell";
import EventEditCard from "./EventEditCard";

export interface EditStartBoatGroupDayProps extends Omit<BoxProps, "onClick"> {
  item: BoatGroup;
  onClick?: (item: EventItem) => void;
  onAddClick?: (item: EventItem) => void;
  eventProps?: Omit<BoxProps, "onClick">;
  isSelectedFunc?: (item: EventItem) => boolean;
}

export default function EditStartBoatGroupDay({
  item,
  onClick,
  onAddClick,
  sx,
  eventProps,
  isSelectedFunc,
  ...props
}: EditStartBoatGroupDayProps) {
  const theme = useTheme();
  const {
    editMode,
    groupEditMode,
    selectEventGroup,
    firstSelectedGroupBoatId,
    selectedEventGroups,
    cal,
    viewMode,
  } = useEventEditorContext();

  // Enable drag-drop if in dragDropUpdate mode.
  const dragModes: Exclude<EditMode, null>[] = ["dragDropUpdate"];
  const enableDrag = !!editMode && dragModes.includes(editMode);
  const { isOver, setNodeRef } = useDroppable({
    id: item.events[0].event_group_id,
    data: item,
    disabled: !enableDrag,
  });
  const style = useMemo(() => {
    return {
      opacity: enableDrag ? (isOver ? 1 : 0.5) : undefined,
    };
  }, [isOver, enableDrag]);

  // Group selection: if a group is already selected, disable selection for non-matching boats.
  const selectionModes: Exclude<EditMode, null>[] = ["groupEdit"];
  const showSelector = !!editMode && selectionModes.includes(editMode);
  let disableSelection = false;
  let cellSx = {};
  if (showSelector && firstSelectedGroupBoatId !== null) {
    if (item.boat?.id !== firstSelectedGroupBoatId) {
      disableSelection = true;
      cellSx = { opacity: 0.3, pointerEvents: "none" };
    }
  }

  const handleSelectGroup = () => {
    if (item.boat?.id) {
      selectEventGroup(item.events[0].event_group_id, item.boat.id);
    }
  };

  const { getBoatData } = useEventCalculator();
  const { boatLimit, boatSx, boatClassName } = getBoatData(item.events[0]);
  const [eventGroup, setEventGroup, eventGroupProps] = useDialog<number>();
  const selected = selectedEventGroups?.includes(item.events[0].event_group_id)
    ? true
    : false;

  return (
    <Box
      className={boatClassName}
      ref={setNodeRef}
      style={style}
      onClick={
        editMode === "singleEdit"
          ? evt => {
              evt.stopPropagation();
              setEventGroup(item.key as any);
            }
          : editMode === "groupEdit" &&
            (groupEditMode === "group" || !groupEditMode)
          ? evt => {
              evt.stopPropagation();
              handleSelectGroup();
            }
          : undefined
      }
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: "1px",
        borderRadius: "5px",
        position: "relative",
        p: 1,
        pt: viewMode === "normal" ? 1 : 2,
        ...(boatSx as any),
        ...sx,
        ...cellSx,
        ...(selected && {
          backgroundImage: `repeating-linear-gradient(
            45deg,
            ${
              theme.palette.mode === "dark"
                ? darken(theme.palette.info.main, 0.4)
                : lighten(theme.palette.info.main, 0.6)
            } 0,
            ${
              theme.palette.mode === "dark"
                ? darken(theme.palette.info.main, 0.6)
                : lighten(theme.palette.info.main, 0.8)
            } 5px,
            transparent 5px,
            transparent 10px
          )!important`,
        }),
      }}
      {...props}
    >
      {viewMode === "normal" && (
        <Toolbar sx={{ gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              top: 0,
              left: 8,
              color: "text.secondary",
            }}
          >
            {item.boat?.name}({item.events[0].duration}p)
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            {moment(item.events[0].start_time).format("LT")}
          </Typography>
          <Spacer right />
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            {boatLimit?.number_of_guests}/{boatLimit?.number_of_places}
          </Typography>
        </Toolbar>
      )}
      {viewMode === "minimal" && (
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            top: -1,
            left: 2,
            right: 2,
            overflow: "hidden",
            color: "text.secondary",
            zIndex: 1,
            textShadow: "0 0 5px rgba(0,0,0,0.5)",
          }}
        >
          {item.boat?.name}
        </Typography>
      )}
      <Toolbar sx={{ gap: 1, flex: 1 }}>
        {item.events.map(event => (
          <EventEditCard
            sx={{ flex: 1 }}
            key={event.id}
            boatLimit={boatLimit}
            item={event}
            isSelected={isSelectedFunc ? isSelectedFunc(event) : undefined}
            onClick={onClick ? () => onClick(event) : undefined}
            onAddClick={onAddClick ? () => onAddClick(event) : undefined}
            {...eventProps}
          />
        ))}
      </Toolbar>
      <Dialog {...eventGroupProps} maxWidth="sm" fullWidth>
        <EventGroupForm
          id={item.events[0].event_group_id as any}
          onClose={eventGroupProps.onClose}
          afterSave={() => {
            cal?.current?.refresh();
            eventGroupProps.onClose &&
              eventGroupProps.onClose({}, "backdropClick");
          }}
        />
      </Dialog>
    </Box>
  );
}
